import { useEffect, useMemo, useState } from "react";
import { Page } from "@iq/models";
import TabView from "../../../CRM/components/TabView/TabView";
import VariableTicketContainer from "../../components/VariableTicketContainer/VariableTicketContainer";
import RelatedTickets from "../../components/VariableTicketPageTabs/RelatedTickets";
import WorkNotes from "../../components/VariableTicketPageTabs/WorkNotes";
import TicketActivity from "../../components/VariableTicketPageTabs/TicketActivity";
import useChangeContext from "../../hooks/useChangeContext";
import { lifecycleStatusOptions } from "../../services/lifecycle_service";

const CHANGE_TABS = ["Related Tickets", "Activity", "Work Notes"];

const PRIORITY_OPTIONS = ["low", "medium", "high", "critical"];
const RISK_OPTIONS = ["low", "medium", "high"];
const APPROVAL_OPTIONS = ["pending", "approved", "rejected", "waived"];

function formatTimestamp(value) {
	if (!value) {
		return "-";
	}

	const parsed = new Date(value);
	if (Number.isNaN(parsed.getTime())) {
		return String(value);
	}

	return parsed.toLocaleString();
}

function toInputDateTimeValue(value) {
	if (!value) {
		return "";
	}
	const parsed = new Date(value);
	if (Number.isNaN(parsed.getTime())) {
		return "";
	}
	const pad = (num) => String(num).padStart(2, "0");
	return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}T${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`;
}

function fromInputDateTimeValue(value) {
	if (!value) {
		return null;
	}
	const parsed = new Date(value);
	if (Number.isNaN(parsed.getTime())) {
		return null;
	}
	return parsed.toISOString();
}

function ChangeDetail({
	changeId,
	initialChange = null,
	teamMembers = [],
	onBack,
	onChangeUpdate,
	onLinkProblem,
	onUnlinkRelation,
	onOpenTicket,
	relationRefreshVersion = 0,
}) {
	const [change, setChange] = useState(initialChange || null);
	const [statusDraft, setStatusDraft] = useState(initialChange?.status || "new");
	const [priorityDraft, setPriorityDraft] = useState(initialChange?.priority || "medium");
	const [riskDraft, setRiskDraft] = useState(initialChange?.risk || "medium");
	const [approvalDraft, setApprovalDraft] = useState(initialChange?.approval_status || "pending");
	const [assigneeDraft, setAssigneeDraft] = useState(
		initialChange?.assigned_to_id != null ? String(initialChange.assigned_to_id) : ""
	);
	const [requesterDraft, setRequesterDraft] = useState(
		initialChange?.requested_by_id != null ? String(initialChange.requested_by_id) : ""
	);
	const [changeTypeDraft, setChangeTypeDraft] = useState(initialChange?.change_type || "");
	const [descriptionDraft, setDescriptionDraft] = useState(initialChange?.description || "");
	const [rollbackDraft, setRollbackDraft] = useState(initialChange?.rollback_plan || "");
	const [plannedStartDraft, setPlannedStartDraft] = useState(toInputDateTimeValue(initialChange?.planned_start_at));
	const [plannedEndDraft, setPlannedEndDraft] = useState(toInputDateTimeValue(initialChange?.planned_end_at));
	const [implementedAtDraft, setImplementedAtDraft] = useState(toInputDateTimeValue(initialChange?.implemented_at));
	const [saving, setSaving] = useState(false);
	const [saveError, setSaveError] = useState("");
	const [unlinkingRelationId, setUnlinkingRelationId] = useState(null);
	const [unlinkError, setUnlinkError] = useState("");
	const [activeTab, setActiveTab] = useState(CHANGE_TABS[0]);
	const {
		context: changeContext,
		loading: loadingChangeContext,
		error: changeContextError,
		refresh: refreshChangeContext,
	} = useChangeContext(changeId);

	useEffect(() => {
		if (relationRefreshVersion > 0) {
			refreshChangeContext();
		}
	}, [relationRefreshVersion, refreshChangeContext]);

	useEffect(() => {
		setChange(initialChange || null);
	}, [initialChange]);

	useEffect(() => {
		setStatusDraft(initialChange?.status || "new");
		setPriorityDraft(initialChange?.priority || "medium");
		setRiskDraft(initialChange?.risk || "medium");
		setApprovalDraft(initialChange?.approval_status || "pending");
		setAssigneeDraft(initialChange?.assigned_to_id != null ? String(initialChange.assigned_to_id) : "");
		setRequesterDraft(initialChange?.requested_by_id != null ? String(initialChange.requested_by_id) : "");
		setChangeTypeDraft(initialChange?.change_type || "");
		setDescriptionDraft(initialChange?.description || "");
		setRollbackDraft(initialChange?.rollback_plan || "");
		setPlannedStartDraft(toInputDateTimeValue(initialChange?.planned_start_at));
		setPlannedEndDraft(toInputDateTimeValue(initialChange?.planned_end_at));
		setImplementedAtDraft(toInputDateTimeValue(initialChange?.implemented_at));
	}, [
		initialChange?.status,
		initialChange?.priority,
		initialChange?.risk,
		initialChange?.approval_status,
		initialChange?.assigned_to_id,
		initialChange?.requested_by_id,
		initialChange?.change_type,
		initialChange?.description,
		initialChange?.rollback_plan,
		initialChange?.planned_start_at,
		initialChange?.planned_end_at,
		initialChange?.implemented_at,
	]);

	const teamMemberMap = useMemo(
		() =>
			new Map(
				(Array.isArray(teamMembers) ? teamMembers : []).map((member) => [
					String(member?.id ?? ""),
					member?.name || member?.email || `User #${member?.id ?? "?"}`,
				])
			),
		[teamMembers]
	);

	const userOptions = useMemo(
		() =>
			(Array.isArray(teamMembers) ? teamMembers : [])
				.map((member) => ({
					id: member?.id,
					label: member?.name || member?.email || `User #${member?.id ?? "?"}`,
				}))
				.filter((option) => option.id != null),
		[teamMembers]
	);

	const displayAssignee =
		change?.assigned_to_id != null && change?.assigned_to_id !== ""
			? teamMemberMap.get(String(change.assigned_to_id)) || `User #${change.assigned_to_id}`
			: "Unassigned";
	const displayRequester =
		change?.requested_by_id != null && change?.requested_by_id !== ""
			? teamMemberMap.get(String(change.requested_by_id)) || `User #${change.requested_by_id}`
			: "Unassigned";

	const linkedIncidentCount = changeContext?.counts?.related_incidents || 0;
	const linkedProblemCount = changeContext?.counts?.problems || 0;
	const statusOptions = lifecycleStatusOptions(change?.status || statusDraft, changeContext?.allowed_transitions);

	function resetDraftFields() {
		setSaveError("");
		setStatusDraft(change?.status || "new");
		setPriorityDraft(change?.priority || "medium");
		setRiskDraft(change?.risk || "medium");
		setApprovalDraft(change?.approval_status || "pending");
		setAssigneeDraft(change?.assigned_to_id != null ? String(change.assigned_to_id) : "");
		setRequesterDraft(change?.requested_by_id != null ? String(change.requested_by_id) : "");
		setChangeTypeDraft(change?.change_type || "");
		setDescriptionDraft(change?.description || "");
		setRollbackDraft(change?.rollback_plan || "");
		setPlannedStartDraft(toInputDateTimeValue(change?.planned_start_at));
		setPlannedEndDraft(toInputDateTimeValue(change?.planned_end_at));
		setImplementedAtDraft(toInputDateTimeValue(change?.implemented_at));
	}

	async function handleSaveFields() {
		if (!changeId || !onChangeUpdate) {
			return;
		}

		setSaving(true);
		setSaveError("");

		const payload = {
			status: statusDraft,
			priority: priorityDraft,
			risk: riskDraft,
			approval_status: approvalDraft,
			assigned_to_id: assigneeDraft ? Number(assigneeDraft) : null,
			requested_by_id: requesterDraft ? Number(requesterDraft) : null,
			change_type: changeTypeDraft,
			description: descriptionDraft,
			rollback_plan: rollbackDraft,
			planned_start_at: fromInputDateTimeValue(plannedStartDraft),
			planned_end_at: fromInputDateTimeValue(plannedEndDraft),
			implemented_at: fromInputDateTimeValue(implementedAtDraft),
		};

		try {
			const updated = await onChangeUpdate(changeId, payload);
			setChange((prev) => ({ ...(prev || {}), ...(updated || {}) }));
			await refreshChangeContext();
		} catch (error) {
			setSaveError(error?.message || "Failed to update change");
		} finally {
			setSaving(false);
		}
	}

	async function handleUnlink({ relationId, ticket, ticketType }) {
		if (!relationId || !onUnlinkRelation) {
			return;
		}

		setUnlinkingRelationId(relationId);
		setUnlinkError("");
		try {
			await onUnlinkRelation({
				relationId,
				tickets: [
					{ type: "change", ticket: change },
					{ type: ticketType, ticket },
				],
			});
			await refreshChangeContext();
		} catch (error) {
			setUnlinkError(error?.message || "Failed to unlink tickets");
		} finally {
			setUnlinkingRelationId(null);
		}
	}

	const hasUnsavedChanges =
		statusDraft !== (change?.status || "new") ||
		priorityDraft !== (change?.priority || "medium") ||
		riskDraft !== (change?.risk || "medium") ||
		approvalDraft !== (change?.approval_status || "pending") ||
		assigneeDraft !== (change?.assigned_to_id != null ? String(change.assigned_to_id) : "") ||
		requesterDraft !== (change?.requested_by_id != null ? String(change.requested_by_id) : "") ||
		changeTypeDraft !== (change?.change_type || "") ||
		descriptionDraft !== (change?.description || "") ||
		rollbackDraft !== (change?.rollback_plan || "") ||
		plannedStartDraft !== toInputDateTimeValue(change?.planned_start_at) ||
		plannedEndDraft !== toInputDateTimeValue(change?.planned_end_at) ||
		implementedAtDraft !== toInputDateTimeValue(change?.implemented_at);

	const subtitle = `${linkedIncidentCount} Related Incident${linkedIncidentCount === 1 ? "" : "s"} · ${linkedProblemCount} Related Problem${linkedProblemCount === 1 ? "" : "s"}`;

	const child1Data = {
		title: change?.title || "Untitled Change",
		subtitle,
		pill: change?.status || "new",
		fields: [
			{ label: "Change ID:", value: change?.number },
			{ label: "Priority:", value: change?.priority },
			{ label: "Risk:", value: change?.risk || "-" },
			{ label: "Approval:", value: change?.approval_status || "-" },
			{ label: "Planned Start:", value: formatTimestamp(change?.planned_start_at) },
			{ label: "Planned End:", value: formatTimestamp(change?.planned_end_at) },
			{ label: "Implemented At:", value: formatTimestamp(change?.implemented_at) },
		],
	};

	const child1ActionButtons = [
		{
			key: "link-problem",
			label: "Link Problem",
			onClick: () => onLinkProblem?.(change),
			visible: Boolean(change?.id),
			disabled: !onLinkProblem,
		},
	];

	const child2Data = {
		inputs: [
			{ label: "Lifecycle Status:", value: statusDraft, placeholder: change?.status ?? "new", type: "dropdown", children: statusOptions, disabled: false, onChange: (value) => setStatusDraft(value) },
			{ label: "Priority:", value: priorityDraft, placeholder: change?.priority ?? "medium", type: "dropdown", children: PRIORITY_OPTIONS, disabled: false, onChange: (value) => setPriorityDraft(value) },
			{ label: "Risk:", value: riskDraft, placeholder: change?.risk ?? "medium", type: "dropdown", children: RISK_OPTIONS, disabled: false, onChange: (value) => setRiskDraft(value) },
			{ label: "Approval:", value: approvalDraft, placeholder: change?.approval_status ?? "pending", type: "dropdown", children: APPROVAL_OPTIONS, disabled: false, onChange: (value) => setApprovalDraft(value) },
			{ label: "Assigned To:", value: assigneeDraft, placeholder: displayAssignee, type: "dropdown", children: userOptions, disabled: false, onChange: (value) => setAssigneeDraft(value) },
			{ label: "Requested By:", value: requesterDraft, placeholder: displayRequester, type: "dropdown", children: userOptions, disabled: false, onChange: (value) => setRequesterDraft(value) },
			{ label: "Change Type:", value: changeTypeDraft, placeholder: "standard", type: "text", disabled: false, onChange: (value) => setChangeTypeDraft(value) },
			{ label: "Planned Start:", value: plannedStartDraft, type: "datetime-local", placeholder: "YYYY-MM-DDTHH:mm", disabled: false, onChange: (value) => setPlannedStartDraft(value), style: "grid-column: 1;" },
			{ label: "Planned End:", value: plannedEndDraft, type: "datetime-local", placeholder: "YYYY-MM-DDTHH:mm", disabled: false, onChange: (value) => setPlannedEndDraft(value) },
			{ label: "Implemented At:", value: implementedAtDraft, type: "datetime-local", placeholder: "YYYY-MM-DDTHH:mm", disabled: false, onChange: (value) => setImplementedAtDraft(value) },
			{ label: "Description:", value: descriptionDraft, placeholder: "No description", type: "textarea", disabled: false, style: "grid-column: 1 / span 2;", onChange: (value) => setDescriptionDraft(value) },
			{ label: "Rollback Plan:", value: rollbackDraft, placeholder: "No rollback plan", type: "textarea", disabled: false, style: "grid-column: span 2;", onChange: (value) => setRollbackDraft(value) },
		],
	};

	const actionButtons = [
		{
			key: "cancel-changes",
			label: "Cancel",
			onClick: resetDraftFields,
			disabled: saving,
			variant: "secondary",
			visible: hasUnsavedChanges,
		},
		{
			key: "save-changes",
			label: saving ? "Saving..." : "Save Changes",
			onClick: handleSaveFields,
			disabled: saving,
			visible: hasUnsavedChanges,
		},
	];

	const views = {
		"Related Tickets": (
			<RelatedTickets
				data={changeContext}
				loading={loadingChangeContext}
				error={unlinkError || changeContextError}
				showChanges={false}
				onOpenTicket={onOpenTicket}
				onUnlink={handleUnlink}
				unlinkingRelationId={unlinkingRelationId}
			/>
		),
		Activity: <TicketActivity ticketType="change" ticketId={changeId} onOpenTicket={onOpenTicket} />,
		"Work Notes": <WorkNotes ticketType="change" ticketId={changeId} />,
	};
	const displayPageTitle = `Change: ${change?.number}`;
	return (
		<Page>
			{saveError ? <p style={{ color: "var(--color-danger)", marginBottom: 12 }}>{saveError}</p> : null}
			<VariableTicketContainer
				title="Change Details"
				child1={child1Data}
				child1ActionButtons={child1ActionButtons}
				child2={child2Data}
				actionButtons={actionButtons}
				child3={
					<TabView
						tabs={CHANGE_TABS}
						views={views}
						activeTab={activeTab}
						onTabChange={setActiveTab}
					/>
				}
			/>
		</Page>
	);
}

export default ChangeDetail;
